import { AxiosError } from 'axios'
import { z } from 'zod'
import { objectToSnake } from 'ts-case-convert'
import { API, handleError, handleFormError, handleZodError } from './api'
import { IN_STOCK, TEMP_OUT, UNAVAILABLE } from '../constants'
import { capitalCase, cleanObject } from '../helpers'
import {
  careLabelSchema,
  fabricAdminSchema,
  fabricAutocompleteSchema,
  fabricCompositionSchema,
  fabricFullSchema,
  fabricLabelSchema,
  fabricMatchesSchema,
  fabricMillsSchema,
  fabricSchema,
  matchMaterialSchema,
  fabricNotificationResponseSchema,
  priceTierSchema,
} from './schemas'

//* ---------------- APIS ---------------- *//
export async function getFabric(id: number) {
  try {
    const response = await API.get(`fabrics/${id}`)
    const fabric = fabricFullSchema.parse(response.data)

    return fabric
  } catch (error) {
    return handleError(error, 'Fabric')
  }
}

export async function getFabrics(rawParams: Record<string, unknown>) {
  try {
    const cleanedParams = cleanObject(rawParams)
    const params = getFabricsParamsSchema.parse(cleanedParams)

    const response = await API.get('fabrics?reverse_sort=true', { params })
    const fabrics = z.array(fabricSchema).parse(response.data)

    return { fabrics, totalFabrics: response.headers.total }
  } catch (error) {
    return handleError(error, 'Fabrics')
  }
}

export async function getAdminFabric(id: number) {
  try {
    const response = await API.get(`fabrics/${id}`)
    const fabric = fabricAdminSchema.parse(response.data)

    return fabric
  } catch (error) {
    return handleError(error, 'Fabric Admin')
  }
}

export async function getAdminFabrics(rawParams: Record<string, unknown>) {
  try {
    const cleanedParams = cleanObject(rawParams)
    const params = getFabricsParamsSchema.parse(cleanedParams)

    const response = await API.get('fabrics', { params: { ...params, reverse_sort: 1, extended: 1 } })
    const fabrics = z.array(fabricAdminSchema).parse(response.data)

    return { fabrics, totalFabrics: response.headers.total }
  } catch (error) {
    return handleError(error, 'Fabrics Admin')
  }
}

export async function getAutocompleteFabrics(rawParams: Record<string, unknown>) {
  try {
    const params = getFabricsParamsSchema.parse(rawParams)

    const response = await API.get('fabrics?reverse_sort=true&autocomplete=true', { params })
    const fabrics = z.array(fabricAutocompleteSchema).parse(response.data)

    return fabrics
  } catch (error) {
    return handleError(error, 'Fabrics')
  }
}

export async function getRelatedFabrics(id: number) {
  try {
    const response = await API.get(`fabrics/${id}/related?rpp=8`)
    const fabrics = z.array(fabricSchema).parse(response.data)

    return fabrics
  } catch (error) {
    return handleError(error, 'Related Fabrics')
  }
}

export async function createFabricFavorite(fabric_id: number) {
  try {
    const response = await API.post('fabric_favorites', { fabric_id })

    return response.data
  } catch (error) {
    return handleError(error, 'Create Fabric Favorite')
  }
}

export async function deleteFabricFavorite(id: number) {
  try {
    const response = await API.delete(`fabric_favorites/${id}`)

    return response.data
  } catch (error) {
    return handleError(error, 'Delete Fabric Favorite')
  }
}

export async function getFabricNotifications(rawParams: Record<string, unknown> = {}) {
  try {
    const params = getFabricNotificationParams.parse(cleanObject(rawParams))
    const response = await API.get('fabric_notifications', { params })
    const notifications = z.array(fabricNotificationResponseSchema).parse(response.data)

    return notifications
  } catch (error) {
    return handleError(error, 'Fabric Notifications')
  }
}

export async function createFabricNotification(rawParams: Record<string, unknown>) {
  try {
    const cleanParams = createFabricNotificationSchema.safeParse(rawParams)

    if (cleanParams.success) {
      await API.post('fabric_notifications', cleanParams.data)
      return { error: false, message: 'You are all set!\nWe will let you know when the fabric is back in stock.' }
    } else {
      const errors = handleZodError(cleanParams.error)
      return { error: true, message: 'Error creating Fabric Notification', fields: errors }
    }
  } catch (error) {
    return handleError(error, 'Create Fabric Notification')
  }
}

export async function updateFabricNotification(id: number, rawParams: Record<string, unknown>) {
  try {
    const data = updateFabricNotificationData.parse(rawParams)
    await API.put(`fabric_notifications/${id}`, data)
    return { error: false, message: 'Fabric Notification Updated' }
  } catch (error) {
    return handleError(error, 'Update Fabric Notifications')
  }
}

export async function deleteFabricNotification(id: number) {
  try {
    await API.delete(`fabric_notifications/${id}`)

    return { error: false, message: 'Fabric Notification Deleted' }
  } catch (error) {
    return handleError(error, 'Delete Fabric Notification')
  }
}

export async function getFabricMatches(fabricId: number) {
  try {
    const response = await API.get(`fabrics/${fabricId}/matches`)
    const matches = fabricMatchesSchema.parse(response.data)

    return matches
  } catch (error) {
    return handleError(error, 'Fabric Matches')
  }
}

export async function getCareLabels() {
  try {
    const response = await API.get('/fabrics/care_labels')
    const careLabels = z.array(careLabelSchema).parse(response.data)

    return careLabels
  } catch (error) {
    return handleError(error, 'Care Labels')
  }
}

export async function getFabricCompositions() {
  try {
    const response = await API.get('/fabrics/compositions', { params: { active: 1, per_page: 5000 } })
    const compositions = z.array(fabricCompositionSchema).parse(response.data)

    return compositions
  } catch (error) {
    return handleError(error, 'Fabric Compositions')
  }
}

export async function getFabricMills() {
  try {
    const response = await API.get('/fabrics/mills')
    const mills = z.array(fabricMillsSchema).parse(response.data)

    return mills
  } catch (error) {
    return handleError(error, 'Fabric Mills')
  }
}

export async function getFabricLabels() {
  try {
    const response = await API.get('/fabrics/labels')
    const labels = z.array(fabricLabelSchema).parse(response.data)

    return labels
  } catch (error) {
    return handleError(error, 'Fabric Labels')
  }
}

type MaterialTypes =
  | 'lining'
  | 'button'
  | 'outerwear_button'
  | 'thread'
  | 'felt'
  | 'suede'
  | 'waistband'
  | 'zipper'
  | 'ribknit'
export async function getFabricMatchesMaterialOptions(materialType: MaterialTypes) {
  try {
    const response = await API.get(`/fabric_matches/materials?material_type=${materialType}`)
    const materials = z.array(matchMaterialSchema).parse(response.data)
    return materials
  } catch (error) {
    return handleError(error, `Fabric Matches Material Options - ${materialType}`)
  }
}

export async function updateFabricAvailability(rawParams: Record<string, unknown>) {
  try {
    const params = updateFabricAvailabilitySchema.parse(rawParams)
    await API.post('fabrics/availability', params)

    return {
      error: false,
      message: `Fabric ${rawParams.supplierFabricNumber} status has been updated to ${capitalCase(
        String(rawParams.status),
      )}`,
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 409) {
      return { error: true, message: String(error.response.data.errors) }
    } else {
      return handleError(error, 'Update Fabric Availability')
    }
  }
}

export async function updateFabric(fabricId: number, rawParams: Record<string, FormDataEntryValue>) {
  try {
    const params = updateFabricEditSchema.parse(rawParams)
    const response = await API.put(`fabrics/${fabricId}`, params)
    const fabric = fabricFullSchema.parse(response.data)
    return { error: false, message: '', fabric }
  } catch (error) {
    return handleFormError(error, 'Update Fabric')
  }
}

export async function updateFabricMatches(fabricId: number, rawParams: Record<string, FormDataEntryValue>) {
  try {
    const params = updateFabricMatchesSchema.parse({ fabricId, ...rawParams })
    await API.post(`/fabric_matches/`, params)
    return { error: false, message: 'Fabric matches updated successfully' }
  } catch (error) {
    return handleFormError(error, 'Update Fabric Matches')
  }
}

//* ---------------- PARAMS ---------------- *//
const getFabricsParamsSchema = z
  .object({
    priceTier: z.array(priceTierSchema).optional(),
    discounted: z.string().optional(),
    namedFilter: z.array(z.string()).optional(),
    fabricType: z.array(z.string()).optional(),
    factory: z.array(z.string()).optional(),
    patternId: z.array(z.string()).optional(),
    primaryColorId: z.array(z.string()).optional(),
    weaveId: z.array(z.string()).optional(),
    hasImage: z.string().optional(),
    q: z.string().optional(),
    page: z.coerce.number().default(1),
    perPage: z.coerce.number().default(24),
    fabricNumber: z.string().optional(),
    show_archived: z.union([z.literal('1'), z.literal('0')]).optional(),
    stockAvailability: z.union([z.literal(IN_STOCK), z.literal(TEMP_OUT), z.literal(UNAVAILABLE)]).optional(),
    collectionId: z.string().optional(),
    extended: z.boolean().optional(),
    showArchived: z.boolean().optional(),
    trimGarmentType: z.coerce.number().optional(),
    fabricPriceCodeId: z.coerce.number().optional(),
    fabricCompositionId: z.coerce.number().optional(),
    supplierId: z.coerce.number().optional(),
    supplierFabricNumber: z.string().optional(),
    fabricMillId: z.coerce.number().optional(),
  })
  .transform(({ stockAvailability, ...data }) => ({
    ...data,
    stockAvailability:
      stockAvailability === IN_STOCK ? 'in_stock' : stockAvailability === TEMP_OUT ? 'temp_out' : stockAvailability,
  }))
  .transform(objectToSnake)

const createFabricNotificationSchema = z
  .object({
    fabricId: z.coerce.number(),
    email: z.string().email(),
    notes: z.string().optional(),
  })
  .transform(({ email, notes, ...data }) => ({
    notificationEmail: email,
    dealerNote: notes,
    ...data,
  }))
  .transform(objectToSnake)

const updateFabricAvailabilitySchema = z
  .object({
    supplierFabricNumber: z.string(),
    inStock: z.union([z.literal(0), z.literal(1)]),
    supplierEnabled: z.union([z.literal(0), z.literal(1)]),
    restockDate: z.coerce.date().optional(),
  })
  .transform(objectToSnake)

/** DATA SCHEMAS */
const updateFabricEditSchema = z
  .object({
    fabricId: z.coerce.number(),
    intent: z.string(),
    active: z.coerce.boolean().optional(),
    description: z.string().optional(),
    fabricCompositionId: z.coerce.number().nullable().optional(),
    premiumLevelId: z.coerce.number().nullable().optional(),
    pattern: z.string().optional(),
    fabricGrouping: z.string().optional(),
    fabricWeight: z.coerce.number().nullable().optional(),
    cuttableWidth: z.coerce.number().nullable().optional(),
    careLabel: z.coerce.number().nullable().optional(),
    supplier: z.coerce.number().nullable().optional(),
    supplierFabricNumber: z.string().optional(),
    supplierPrice: z.coerce.number().nullable().optional(),
    millName: z.coerce.number().nullable().optional(),
    label: z.coerce.number().nullable().optional(),
  })
  .omit({ fabricId: true, intent: true })
  .transform(objectToSnake)
  .transform(({ supplier, mill_name, care_label, label, ...data }) => ({
    supplier_id: supplier,
    mill_id: mill_name,
    care_label_id: care_label,
    label_id: label,
    ...data,
  }))

const updateFabricMatchesSchema = z
  .object({
    fabricId: z.coerce.number(),
    liningId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    buttonId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    feltId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    suedeId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    threadId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    zipperId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    ribknitId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    outerwearButtonId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
    waistbandId: z
      .string()
      .transform(v => (v === '' ? null : Number(v)))
      .nullable()
      .optional(),
  })
  .transform(({ liningId, ...rest }) => ({
    liningFabricId: liningId,
    ...rest,
  }))
const getFabricNotificationParams = z
  .object({
    fabricId: z.coerce.number().optional(),
    email: z.string().email().optional(),
    perPage: z.number().default(25),
  })
  .transform(objectToSnake)

const updateFabricNotificationData = z
  .object({ email: z.string().email().optional(), notes: z.string().optional() })
  .transform(objectToSnake)

//* ---------------- HELPERS ---------------- *//
